import Constants from "expo-constants";
import { getAppConfig } from "./globals";

/**
 * Resolves the base URL corresponding to the current deployment environment.
 * - LOCAL / TEST: http://localhost:5000/
 * - DEV / STAGING: https://dev.memories.on247.net/
 * - PROD / PRODUCTION: https://memories.on247.net/
 *
 * @param {string} [explicitEnv] Optional environment override (e.g. "LOCAL", "DEV", "PROD").
 * @returns {string} The base URL with a trailing slash.
 */
export function getEnvironmentBaseUrl(explicitEnv?: string): string {
  let envName = explicitEnv;

  if (!envName) {
    try {
      envName = getAppConfig().environ.getName();
    } catch {
      const extraEnv = Constants.expoConfig?.extra?.env || process.env.APP_VARIANT || process.env.ENV_VAR;
      if (extraEnv) {
        envName = String(extraEnv);
      } else if (typeof window !== "undefined" && window.location) {
        const host = window.location.hostname;
        if (host === "localhost" || host === "127.0.0.1") {
          envName = "LOCAL";
        } else if (host.includes("memories.on247.net") && !host.includes("dev.")) {
          envName = "PROD";
        } else {
          envName = "DEV";
        }
      } else {
        envName = "LOCAL";
      }
    }
  }

  const normalized = (envName || "").toUpperCase().trim();

  if (normalized === "LOCAL" || normalized === "TEST") {
    return "http://localhost:5000/";
  }

  if (normalized === "PROD" || normalized === "PRODUCTION") {
    return "https://memories.on247.net/";
  }

  // Default to DEV for development, e2e, uat, qa, or unassigned variants
  return "https://dev.memories.on247.net/";
}

/**
 * Returns the environment-appropriate Privacy Policy URL.
 *
 * @param {string} [explicitEnv] Optional environment override.
 * @returns {string} Full URL to the Privacy Policy.
 */
export function getPrivacyPolicyUrl(explicitEnv?: string): string {
  const base = getEnvironmentBaseUrl(explicitEnv);
  return `${base.replace(/\/+$/, "")}/privacy-policy`;
}

/**
 * Returns the environment-appropriate About page URL.
 *
 * @param {string} [explicitEnv] Optional environment override.
 * @returns {string} Full URL to the About page.
 */
export function getAboutUrl(explicitEnv?: string): string {
  const base = getEnvironmentBaseUrl(explicitEnv);
  return `${base.replace(/\/+$/, "")}/about`;
}
